import React, { useMemo } from 'react';
import { useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UserRole } from 'context';
import { Icon, IconNames, ITab } from 'atoms';
import { LessonType } from 'types';

import styled from 'styled-components';
import { PATHS } from 'router/config';
import { getIframeUrlForCourse } from '../../../config/courseIframeConfig';

type ILessonTabs = {
  pathToDescription: string;
  pathToTasks: string;
  tabs: ITab[];
};

interface ILessonTabsVariables {
  groupId?: string;
  lessonId?: string;
  userRole?: string | null;
  currentLesson?: LessonType | null;
  dateIsNow?: boolean;
  dateIsTomorrow?: boolean;
  courseName?: string;
  lessonsCount?: number;
  /**
   * Группа «без расписания»: блокировки вкладок остаются прежними, но их
   * подсказки не должны называть день занятия.
   */
  hideSchedule?: boolean;
}

export const useLessonTabs = ({
  groupId,
  lessonId,
  userRole,
  currentLesson,
  dateIsNow,
  dateIsTomorrow,
  courseName,
  lessonsCount,
  hideSchedule = false,
}: ILessonTabsVariables): ILessonTabs => {
  const { t } = useTranslation();
  const pathToDescription = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonDescription}`,
    [groupId, lessonId],
  );

  const pathToStudentMaterials = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonStudentAttachments}`,
    [groupId, lessonId],
  );

  // const pathToStudentResources = useMemo(
  //   () => `/courses/${groupId}/${lessonId}/${PATHS.lessonStudentResources}`,
  //   [groupId, lessonId],
  // );

  const pathToTeacherMaterials = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonTeacherAttachments}`,
    [groupId, lessonId],
  );

  const pathToJournal = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonJournal}`,
    [groupId, lessonId],
  );

  const pathToTasks = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonTasks}`,
    [groupId, lessonId],
  );

  const pathToResults = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonResults}`,
    [groupId, lessonId],
  );

  const pathToFinalTest = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonFinalTest}`,
    [groupId, lessonId],
  );

  const pathToWhiteboard = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonWhiteboard}`,
    [groupId, lessonId],
  );

  const pathToLessonHomework = useMemo(
    () => `/courses/${groupId}/${lessonId}/${PATHS.lessonHomework}`,
    [groupId, lessonId],
  );

  const isLastLesson = currentLesson?.index === lessonsCount;
  const iframeUrl = courseName ? getIframeUrlForCourse(courseName) : null;
  const showFinalTest = isLastLesson && Boolean(iframeUrl);

  const isTasksTabActive = useMatch({ path: pathToTasks, end: false });

  const journalTab = useMemo(
    () =>
      currentLesson?.needToFinish ? (
        <TabText>
          {t('lesson.tabs.journal')}
          <IconStyled name={IconNames.WarningYellow} width={16} height={16} />
        </TabText>
      ) : (
        `${t('lesson.tabs.journal')}`
      ),
    [currentLesson?.needToFinish, t],
  );

  const teacherTabs: ITab[] = useMemo(
    () => [
      {
        children: `${t('lesson.tabs.lesson')}`,
        to: pathToDescription,
      },
      ...(currentLesson?.hasTeacherAttachment
        ? [
            {
              children: `${t('lesson.tabs.attachments')}`,
              to: pathToTeacherMaterials,
            },
          ]
        : []),
      {
        children: journalTab,
        to: pathToJournal,
        isEnabled: currentLesson?.isEnded || dateIsNow || dateIsTomorrow,
        enabledText: `${t('lesson.tabs.journalTooltip')}`,
      },
      // rev.3: homework is gradeable on every non-diploma lesson, so the entry
      // tab must open whenever there are task types OR homework is available —
      // otherwise the teacher can't reach the homework roster on a plain lesson.
      ...(currentLesson?.taskTypes.length || currentLesson?.homeworkAvailable
        ? [
            {
              children: `${t('lesson.tabs.taskEvaluation')}`,
              to: pathToTasks,
              isEnabled: currentLesson?.isEnded || dateIsNow || dateIsTomorrow,
              enabledText: `${t('lesson.tabs.taskEvaluationTooltip')}`,
              isActive: Boolean(isTasksTabActive),
            },
          ]
        : []),
      // Teacher always sees the whiteboard tab — even before a board exists
      // (the tab hosts the "new / continue existing" start panel).
      {
        children: `${t('lesson.tabs.whiteboard')}`,
        to: pathToWhiteboard,
      },
      ...(showFinalTest
        ? [{ children: `${t('lesson.tabs.finalTest')}`, to: pathToFinalTest }]
        : []),
    ],
    [
      currentLesson?.isEnded,
      currentLesson?.hasTeacherAttachment,
      currentLesson?.taskTypes,
      currentLesson?.homeworkAvailable,
      pathToDescription,
      pathToTeacherMaterials,
      pathToJournal,
      pathToTasks,
      pathToFinalTest,
      pathToWhiteboard,
      isTasksTabActive,
      showFinalTest,
      dateIsNow,
      dateIsTomorrow,
      journalTab,
      t,
    ],
  );

  const studentTabs: ITab[] = useMemo(
    () => [
      {
        children: `${t('lesson.tabs.lesson')}`,
        to: pathToDescription,
      },
      ...(currentLesson?.hasStudentAttachment
        ? [
            {
              children: `${t('lesson.tabs.attachments')}`,
              to: pathToStudentMaterials,
              isEnabled: currentLesson?.isEnded || dateIsNow || dateIsTomorrow,
              enabledText: hideSchedule
                ? `${t('lesson.tabs.attachmentsTooltipNoDate')}`
                : `${t('lesson.tabs.attachmentsTooltip')}`,
            },
          ]
        : []),
      // {
      //   children: `${t('lesson.tabs.resources')}`,
      //   to: pathToStudentResources,
      // },
      ...(currentLesson?.hasStudentMarks
        ? [
            {
              children: `${t('lesson.tabs.taskResults')}`,
              to: pathToResults,
            },
          ]
        : []),
      // Student: read-only board. `whiteboardId` is already null for students
      // until the lesson is over (the server applies the same rule that gates
      // the scene itself), so availability is NEVER decided client-side —
      // `isEnded` is day-granular and would hide the board for the rest of the
      // lesson day.
      //
      // When there is no board yet we still render the tab DISABLED with an
      // explanation, because silently omitting it left a student who watched
      // the teacher draw with no way to find the board and no idea it was
      // coming. `isEnded` is used ONLY to decide whether to show that
      // placeholder — once the lesson is over and the server still reports no
      // board, none was made, so we promise nothing and hide the tab.
      // eslint-disable-next-line no-nested-ternary
      ...(currentLesson?.whiteboardId
        ? [
            {
              children: `${t('lesson.tabs.whiteboard')}`,
              to: pathToWhiteboard,
            },
          ]
        : !currentLesson?.isEnded
        ? [
            {
              children: `${t('lesson.tabs.whiteboard')}`,
              to: pathToWhiteboard,
              isEnabled: false,
              enabledText: `${t('lesson.tabs.whiteboardTooltip')}`,
            },
          ]
        : []),
      // rev.3: Homework tab gated by the server field `homeworkAvailable`
      // (every non-diploma lesson — a teacher assignment is no longer required;
      // the brief may live in the lesson materials). No client-side date logic.
      ...(currentLesson?.homeworkAvailable
        ? [
            {
              children: `${t('lesson.tabs.homework')}`,
              to: pathToLessonHomework,
            },
          ]
        : []),
      ...(showFinalTest
        ? [{ children: `${t('lesson.tabs.finalTest')}`, to: pathToFinalTest }]
        : []),
    ],
    [
      currentLesson?.isEnded,
      currentLesson?.hasStudentAttachment,
      currentLesson?.hasStudentMarks,
      currentLesson?.whiteboardId,
      currentLesson?.homeworkAvailable,
      pathToDescription,
      pathToStudentMaterials,
      // pathToStudentResources,
      pathToResults,
      pathToFinalTest,
      pathToWhiteboard,
      pathToLessonHomework,
      showFinalTest,
      dateIsNow,
      dateIsTomorrow,
      hideSchedule,
      t,
    ],
  );

  const methodistTabs: ITab[] = useMemo(
    () => [
      {
        children: `${t('lesson.tabs.lesson')}`,
        to: pathToDescription,
      },
      ...(currentLesson?.hasStudentAttachment
        ? [
            {
              children: `${t('lesson.tabs.attachmentsStudent')}`,
              to: pathToStudentMaterials,
              isEnabled: true,
            },
          ]
        : []),
      ...(currentLesson?.hasTeacherAttachment
        ? [
            {
              children: `${t('lesson.tabs.attachmentsTeacher')}`,
              to: pathToTeacherMaterials,
              isEnabled: true,
            },
          ]
        : []),
      // Methodist: read-only board whenever the lesson has one.
      ...(currentLesson?.whiteboardId
        ? [
            {
              children: `${t('lesson.tabs.whiteboard')}`,
              to: pathToWhiteboard,
            },
          ]
        : []),
      // rev.3: Methodist read-only homework tab on every non-diploma lesson
      // (server gate `homeworkAvailable`), regardless of a teacher assignment.
      ...(currentLesson?.homeworkAvailable
        ? [
            {
              children: `${t('lesson.tabs.homework')}`,
              to: pathToLessonHomework,
            },
          ]
        : []),
      ...(showFinalTest
        ? [{ children: `${t('lesson.tabs.finalTest')}`, to: pathToFinalTest }]
        : []),
    ],
    [
      currentLesson?.hasStudentAttachment,
      currentLesson?.hasTeacherAttachment,
      currentLesson?.whiteboardId,
      currentLesson?.homeworkAvailable,
      pathToDescription,
      pathToStudentMaterials,
      pathToTeacherMaterials,
      pathToFinalTest,
      pathToWhiteboard,
      pathToLessonHomework,
      showFinalTest,
      t,
    ],
  );

  const TABS = useMemo(() => {
    switch (userRole) {
      case UserRole.TEACHER:
        return teacherTabs;
      case UserRole.STUDENT:
        return studentTabs;
      default:
        return methodistTabs;
    }
  }, [userRole, teacherTabs, studentTabs, methodistTabs]);

  return {
    pathToDescription,
    pathToTasks,
    tabs: TABS,
  };
};

const TabText = styled.div`
  display: flex;
  align-items: center;
`;

const IconStyled = styled((props) => <Icon {...props} />)`
  margin-left: 4px;
`;
